// src/pages/admin/tabs/RegionsTab.jsx
import React from 'react';
import styled from 'styled-components';
import { AdminAPI } from '../../../api/admin';

const Wrap = styled.div`display:grid; gap:20px;`;
const Card = styled.div`
  border:1px solid var(--bdr); background:var(--card); border-radius:14px; padding:16px; box-shadow:var(--shadow);
`;
const BlockTitle = styled.h3`margin:0 0 12px;`;
const Row = styled.div`display:flex; gap:10px; flex-wrap:wrap; margin-bottom:10px; align-items:center;`;
const Input = styled.input`
  padding:10px 12px; border:1px solid var(--bdr); border-radius:10px; background:var(--bg);
`;
const Num = styled(Input).attrs({ type:'number' })``;
const Checkbox = styled.input.attrs({ type:'checkbox' })``;
const Btn = styled.button`
  padding:10px 14px; border-radius:12px; border:1px solid var(--bdr);
  background:var(--card); font-weight:700; cursor:pointer;
  &[data-primary="true"]{ background:var(--primary); color:var(--primaryFg); border-color:transparent; }
  &[disabled]{ opacity:.6; cursor:default; }
`;
const Table = styled.table`
  width:100%; border-collapse:collapse;
  th, td{ border-bottom:1px solid var(--bdr); padding:10px 8px; text-align:left; }
  th{ font-weight:700; }
`;

export default function RegionsTab(){
  const [regions, setRegions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [creating, setCreating] = React.useState(false);

  // form state
  const [nameRu, setNameRu] = React.useState('');
  const [nameEn, setNameEn] = React.useState('');
  const [code,   setCode]   = React.useState('');
  const [isActive, setIsActive] = React.useState(true);

  // edit form
  const [editing, setEditing] = React.useState(null); // {id, name_ru, name_en, code, is_active} | null
  const [editBusy, setEditBusy] = React.useState(false);

  const fetchRegions = React.useCallback(async ()=>{
    setLoading(true);
    try{
      const j = await AdminAPI.listRegions();
      setRegions(j.regions || []);
    }catch(err){
      console.error(err);
      alert('Не удалось загрузить регионы');
    }finally{
      setLoading(false);
    }
  },[]);

  React.useEffect(()=>{ fetchRegions(); }, [fetchRegions]);

  const createRegion = async ()=>{
    if(!nameRu && !nameEn){ alert('Укажите название (RU или EN)'); return; }
    if(!code){ alert('Укажите code (например: MOS, SPB)'); return; }

    setCreating(true);
    try{
      const payload = {
        name_ru: nameRu || undefined,
        name_en: nameEn || undefined,
        code: code.trim(),
        is_active: !!isActive,
      };
      await AdminAPI.createRegion(payload);
      // reset form
      setNameRu(''); setNameEn(''); setCode(''); setIsActive(true);
      await fetchRegions();
    }catch(err){
      console.error(err);
      const details = err?.payload?.details ? JSON.stringify(err.payload.details) : (err.message || 'Ошибка создания');
      alert(`Не удалось создать регион: ${details}`);
    }finally{
      setCreating(false);
    }
  };

  const deleteRegion = async (id)=>{
    if(!window.confirm('Удалить регион? Если есть районы/объекты — удаление будет отклонено.')) return;
    try{
      await AdminAPI.deleteRegion(id);
      await fetchRegions();
    }catch(err){
      console.error(err);
      alert(err?.payload?.details || err?.message || 'Ошибка удаления');
    }
  };

  const toggleActive = async (r)=>{
    try{
      await AdminAPI.updateRegion(r.id, { is_active: !r.is_active });
      await fetchRegions();
    }catch(err){
      alert(err?.payload?.details || err?.message || 'Не удалось изменить статус');
    }
  };

  const startEdit = (r)=>{
    setEditing({
      id: r.id,
      name_ru: r.name_ru || '',
      name_en: r.name_en || '',
      code: r.code || '',
      is_active: !!r.is_active,
    });
  };
  const cancelEdit = ()=> setEditing(null);
  const saveEdit = async ()=>{
    if (!editing) return;
    const { id, name_ru, name_en, code, is_active } = editing;
    if (!name_ru && !name_en){ alert('Укажите название (RU или EN)'); return; }
    if (!code){ alert('Укажите code (например: MOS, SPB)'); return; }
    setEditBusy(true);
    try{
      await AdminAPI.updateRegion(id, {
        name_ru: name_ru || undefined,
        name_en: name_en || undefined,
        code: code.trim(),
        is_active: !!is_active,
      });
      setEditing(null);
      await fetchRegions();
    }catch(err){
      alert(err?.payload?.details || err?.message || 'Ошибка сохранения');
    }finally{
      setEditBusy(false);
    }
  };

  return (
    <Wrap>
      <Card>
        <BlockTitle>Добавить регион</BlockTitle>
        <Row>
          <Input placeholder="Название RU" value={nameRu} onChange={e=>setNameRu(e.target.value)} />
          <Input placeholder="Название EN" value={nameEn} onChange={e=>setNameEn(e.target.value)} />
          <Input placeholder="Код (MOS, SPB...)" value={code} onChange={e=>setCode(e.target.value.toUpperCase())} />
          <div style={{display:'inline-flex', alignItems:'center', gap:8}}>
            <span style={{opacity:.8}}>Активен:</span>
            <Btn
              data-primary={isActive ? 'true' : undefined}
              onClick={()=>setIsActive(true)}
              disabled={isActive}
            >
              Да
            </Btn>
            <Btn
              data-primary={!isActive ? 'true' : undefined}
              onClick={()=>setIsActive(false)}
              disabled={!isActive}
            >
              Нет
            </Btn>
          </div>
          <Btn data-primary="true" onClick={createRegion} disabled={creating}>
            {creating ? 'Сохранение…' : 'Добавить'}
          </Btn>
        </Row>
      </Card>

      {editing && (
        <Card>
          <BlockTitle>Редактировать регион #{editing.id}</BlockTitle>
          <Row>
            <Input placeholder="Название RU" value={editing.name_ru} onChange={e=>setEditing(ed=>({...ed, name_ru:e.target.value}))} />
            <Input placeholder="Название EN" value={editing.name_en} onChange={e=>setEditing(ed=>({...ed, name_en:e.target.value}))} />
            <Input placeholder="Код (MOS, SPB...)" value={editing.code} onChange={e=>setEditing(ed=>({...ed, code:e.target.value.toUpperCase()}))} />
            <div style={{display:'inline-flex', alignItems:'center', gap:8}}>
              <span style={{opacity:.8}}>Активен:</span>
              <Btn
                data-primary={editing.is_active ? 'true' : undefined}
                onClick={()=>setEditing(ed=>({...ed, is_active:true}))}
                disabled={!!editing.is_active}
              >
                Да
              </Btn>
              <Btn
                data-primary={!editing.is_active ? 'true' : undefined}
                onClick={()=>setEditing(ed=>({...ed, is_active:false}))}
                disabled={!editing.is_active}
              >
                Нет
              </Btn>
            </div>
            <Btn data-primary="true" onClick={saveEdit} disabled={editBusy}>{editBusy ? 'Сохранение…' : 'Сохранить'}</Btn>
            <Btn onClick={cancelEdit} disabled={editBusy}>Отмена</Btn>
          </Row>
        </Card>
      )}

      <Card>
        <BlockTitle>Регионы — список {loading ? '(загрузка...)' : ''}</BlockTitle>
        <Table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Название</th>
              <th>Code</th>
              <th>Активен</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {regions.map(r=>(
              <tr key={r.id}>
                <td>{r.id}</td>
                <td>{r.name_ru} / {r.name_en}</td>
                <td>{r.code}</td>
                <td>
                  <Btn onClick={()=>toggleActive(r)} title="Переключить активность">
                    {r.is_active ? 'Да' : 'Нет'}
                  </Btn>
                </td>
                <td>
                  <Btn onClick={()=>startEdit(r)}>Редактировать</Btn>{' '}
                  <Btn onClick={()=>deleteRegion(r.id)}>Удалить</Btn>
                </td>
              </tr>
            ))}
            {!regions.length && !loading && (
              <tr><td colSpan={6} style={{opacity:.7}}>Нет регионов</td></tr>
            )}
          </tbody>
        </Table>
      </Card>
    </Wrap>
  );
}
